/**
 * App.js - OnTimeLog 프로젝트 투입시간 관리 시스템
 * OnBoard 스타일 UI/UX 정책 기준 완전 반영
 */

import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import MobileLayout from './layouts/MobileLayout';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline, GlobalStyles, Box } from '@mui/material';
import { ThemeProvider, useTheme as useCustomTheme } from './contexts/ThemeContext';
import { MobileViewProvider, useMobileView } from './contexts/MobileViewContext';
import { UserRoleProvider } from './contexts/UserRoleContext';
import { LanguageProvider } from './contexts/LanguageContext';
import notificationService from './services/NotificationService';
import DailyTimeNotificationModal from './components/DailyTimeNotificationModal';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { DBConnectionProvider } from './contexts/DBConnectionContext';
import { TaskProvider } from './contexts/TaskContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { checkAuthStatus, clearAuthData, getUserFromToken } from './utils/auth';
import { detectMobileDevice, convertToMobilePath, convertToPcPath } from './utils/deviceDetection';
import { OnboardingProvider } from './contexts/OnboardingContext';
import OnboardingTutorial from './components/OnboardingTutorial';
import axios from 'axios';

// Layouts
import MainLayout from './layouts/MainLayout';
import LoadingSplash from './components/LoadingSplash';

// Routes
import routes from './routes';

// 전역에서 접근 가능하도록 설정 (스케줄러 재시작용)
if (typeof window !== 'undefined') {
  window.notificationService = notificationService;
}

// axios interceptor 설정: 모든 요청에 Authorization 헤더 자동 추가
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // JWT 토큰인 경우 Bearer 토큰으로 전송
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// axios response interceptor: 토큰 만료 시 자동 갱신
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // 401 에러이고, 아직 재시도하지 않은 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // 리프레시 토큰이 없으면 로그아웃
        clearAuthData();
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      try {
        // 리프레시 토큰으로 새 액세스 토큰 발급
        const response = await axios.post(`/api/v1/auth/refresh`, {
          refresh_token: refreshToken
        });
        
        const { access_token } = response.data;
        localStorage.setItem('token', access_token);
        
        // 원래 요청 재시도
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        // 리프레시 실패 시 로그아웃
        clearAuthData();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// AppContent - ThemeContext를 사용하는 내부 컴포넌트
function AppContent() {
  const { isDark, colors } = useCustomTheme();
  const [isLoading, setIsLoading] = React.useState(true);
  const [dbStatus, setDbStatus] = React.useState(null); // DB 연결 상태
  const [dbCheckComplete, setDbCheckComplete] = React.useState(false);
  
  // 스플래시 화면 설정
  const skipSplash = sessionStorage.getItem('skipAppSplash') === 'true';
  const [showSplash, setShowSplash] = React.useState(!skipSplash);

  // DB 상태 확인 (스플래시 화면에서 실행)
  React.useEffect(() => {
    if (showSplash && !dbCheckComplete) {
      checkDatabaseHealth();
    }
  }, [showSplash, dbCheckComplete]);

  // V30: NAS 등에서 스플래시가 끝나지 않는 무한루프 방지 - 최대 시간 후 강제 종료
  const SPLASH_MAX_MS = 10000; // 10초
  React.useEffect(() => {
    if (!showSplash) return;
    const t = setTimeout(() => {
      setShowSplash(false);
      setIsLoading(false);
      setDbCheckComplete(true);
      sessionStorage.setItem('skipAppSplash', 'true');
    }, SPLASH_MAX_MS);
    return () => clearTimeout(t);
  }, [showSplash]);

  const checkDatabaseHealth = async () => {
    // V30: NAS 배포 시 동일 오리진 기준 상대 경로(/health), 타임아웃으로 무한 대기 방지
    const healthUrl = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/health` : '/health';
    try {
      const response = await axios.get(healthUrl, {
        timeout: 8000, // 8초 (NAS/느린 환경)
        validateStatus: (status) => status === 200 || status === 503
      });
      
      const healthData = response.data;
      setDbStatus({
        connected: healthData.database?.connected || false,
        status: healthData.status || 'unknown',
        database: healthData.database?.database || 'unknown',
        host: healthData.database?.host || 'unknown',
        accessLogTable: healthData.tables?.access_log || false,
        error: healthData.database?.error || null
      });
      
      // DB 연결 상태 확인 (에러만 로깅)
      if (!healthData.database?.connected) {
        console.warn('⚠️ DB 연결 상태 확인 실패:', healthData.database?.error || '연결되지 않음');
      }
    } catch (error) {
      console.error('❌ DB 상태 확인 실패:', error.message);
      setDbStatus({
        connected: false,
        status: 'error',
        error: error.message || '백엔드 서버에 연결할 수 없습니다'
      });
    } finally {
      setDbCheckComplete(true);
    }
  };

  const splashStages = React.useMemo(() => {
    // DB 상태에 따라 메시지 동적 변경
    const dbMessage = dbStatus?.connected 
      ? '데이터베이스 연결 완료' 
      : dbStatus?.error 
        ? '데이터베이스 연결 확인 중...' 
        : '데이터베이스 연결 중...';
    
    if (isDark) {
      return [
        { progress: 12, message: '시스템 초기화 중...' },
        { progress: 32, message: dbMessage },
        { progress: 55, message: '투입시간 관리 모듈 로딩...' },
        { progress: 78, message: '대시보드 준비 중...' },
        { progress: 92, message: '권한 확인 중...' },
        { progress: 100, message: dbStatus?.connected ? 'OnTimeLog 시스템 준비 완료' : '시스템 준비 중...' }
      ];
    }
    return [
      { progress: 10, message: '시스템 연결 중...' },
      { progress: 28, message: dbMessage },
      { progress: 48, message: '투입시간 데이터 로딩...' },
      { progress: 68, message: '대시보드 초기화 중...' },
      { progress: 88, message: '메뉴 준비 중...' },
      { progress: 100, message: dbStatus?.connected ? '환영합니다! OnTimeLog에 접속 중' : '시스템 준비 중...' }
    ];
  }, [isDark, dbStatus]);

  const splashDuration = React.useMemo(() => {
    // DB 확인이 완료되면 스플래시 지속 시간 조정
    if (dbCheckComplete) {
      return isDark ? 2000 : 1800; // DB 확인 완료 시 약간 단축
    }
    return isDark ? 2800 : 2500;
  }, [isDark, dbCheckComplete]);

  React.useEffect(() => {
    if (!showSplash && isLoading) {
      setIsLoading(false);
    }
  }, [showSplash, isLoading, setIsLoading]);

  // MUI 테마 생성 (다크/라이트 모드 동적 적용)
  const muiTheme = React.useMemo(() => createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: colors.accent,
        light: isDark ? '#B5A5FF' : '#A594F9',
        dark: isDark ? '#7B68EE' : '#6854DB',
      },
      secondary: {
        main: isDark ? '#FF7AB8' : '#FF6B9D',
        light: isDark ? '#FF9CD1' : '#FF8FB8',
        dark: isDark ? '#FF5E94' : '#FF4F8A',
      },
      success: {
        main: colors.success,
      },
      error: {
        main: colors.error,
      },
      warning: {
        main: colors.warning,
      },
      info: {
        main: colors.info,
      },
      background: {
        default: colors.primaryBg,
        paper: colors.tertiaryBg,
      },
      text: {
        primary: colors.primaryText,
        secondary: colors.secondaryText,
      },
      divider: colors.primaryBorder,
    },
    typography: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Noto Sans KR', 'Malgun Gothic', 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
      h1: {
        fontSize: '24px',
        fontWeight: 700,
        letterSpacing: '-0.01em',
      },
      h2: {
        fontSize: '20px',
        fontWeight: 600,
        letterSpacing: '0em',
      },
      h3: {
        fontSize: '18px',
        fontWeight: 600,
      },
      body1: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: 1.6,
      },
      button: {
        fontSize: '14px',
        fontWeight: 500,
        textTransform: 'none',
        letterSpacing: '0.01em',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            padding: '8px 16px',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            background: isDark ? 'linear-gradient(140deg, rgba(32, 34, 42, 0.96) 0%, rgba(18, 19, 26, 0.88) 100%)' : '#FFFFFF',
            backdropFilter: isDark ? 'blur(14px)' : 'none',
            WebkitBackdropFilter: isDark ? 'blur(14px)' : 'none',
            border: `1px solid ${isDark ? 'rgba(158, 164, 189, 0.28)' : 'rgba(221, 225, 230, 0.9)'}`,
            boxShadow: isDark
              ? '0 28px 48px -18px rgba(0, 0, 0, 0.82), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
              : '0 12px 28px -16px rgba(15, 29, 68, 0.12)',
            borderRadius: 18,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: isDark
                ? '0 32px 58px -16px rgba(0, 0, 0, 0.88), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
                : '0 18px 34px -18px rgba(15, 29, 68, 0.16)',
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.secondaryBg,
            borderRight: `1px solid ${colors.primaryBorder}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.Mui-selected': {
              backgroundColor: theme.palette.action.selected,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            },
            '&:hover': {
              backgroundColor: colors.hoverBg,
            },
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& input[type="date"]::-webkit-calendar-picker-indicator': {
              filter: isDark ? 'invert(1)' : 'none',
              cursor: 'pointer',
              opacity: 1,
            },
            '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
              filter: isDark ? 'invert(1)' : 'none',
              cursor: 'pointer',
              opacity: 1,
            },
          },
        },
      },
    },
  }), [isDark, colors]);

  // 전역 스타일
  const globalStyles = (
    <GlobalStyles
      styles={{
        '@import': "url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;600;700;900&display=swap')",
        
        '::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
          background: isDark ? '#404040' : '#D9D9D9',
          borderRadius: '3px',
          '&:hover': {
            background: isDark ? '#505050' : '#BFBFBF',
          },
        },
        
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: isDark ? '#404040 transparent' : '#D9D9D9 transparent',
        },
        
        body: {
          margin: 0,
          padding: 0,
          fontFamily: "'Noto Sans KR', 'Roboto', sans-serif",
          backgroundColor: colors.primaryBg,
          color: colors.primaryText,
          overflow: 'hidden',
          // 모바일 경로에서는 스크롤 허용
          '&.mobile-route': {
            overflow: 'auto',
            height: '100%',
          },
        },
        
        '::selection': {
          backgroundColor: colors.accent,
          color: '#FFFFFF',
        },
        
        // 웹킷 캘린더 피커 인디케이터 전역 스타일 (다크모드에서 흰색으로 표시)
        'input[type="date"]::-webkit-calendar-picker-indicator': {
          filter: isDark ? 'invert(1)' : 'none',
          cursor: 'pointer',
          opacity: 1,
        },
        '.MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
          filter: isDark ? 'invert(1)' : 'none',
          cursor: 'pointer',
          opacity: 1,
        },
        'input[type="date"].MuiInputBase-input::-webkit-calendar-picker-indicator': {
          filter: isDark ? 'invert(1)' : 'none',
          cursor: 'pointer',
          opacity: 1,
        },
        
        a: {
          color: colors.accent,
          textDecoration: 'none',
          '&:hover': {
            color: colors.accentHover,
            textDecoration: 'underline',
          },
        },
        
        // 모바일 뷰 스타일 (Mobile First 전략)
        'body.mobile-view': {
          // 기준: iPhone 12/13/14 Pro (390px x 844px)
          maxWidth: '390px',
          margin: '0 auto',
          
          // 터치 최적화: 폰트 및 버튼 크기 확대
          fontSize: '16px', // 최소 16px로 iOS 줌 방지
          lineHeight: '1.6', // 가독성을 위한 넓은 줄 간격
          
          // 수직 스택 레이아웃 (Vertical Stack)
          display: 'flex',
          flexDirection: 'column',
          
          // 타이포그래피: 본문 텍스트 기본 스타일
          'p, span, div, .MuiTypography-body1, .MuiTypography-body2': {
            fontSize: '16px !important',
            lineHeight: '1.6 !important',
          },
          
          // 모든 버튼 최소 크기 확대 (터치 용이성 - 44px x 44px)
          'button, .MuiButton-root, .MuiIconButton-root': {
            minHeight: '44px !important',
            minWidth: '44px !important',
            padding: '12px 16px !important',
            touchAction: 'manipulation', // 더블탭 줌 방지
            fontSize: '16px !important',
          },
          
          // 텍스트 입력 필드 크기 확대 (수직 레이아웃)
          'input, textarea, .MuiTextField-root input, .MuiTextField-root textarea': {
            fontSize: '16px !important', // iOS 줌 방지
            minHeight: '44px !important',
            padding: '12px 16px !important',
            touchAction: 'manipulation',
            lineHeight: '1.5 !important',
          },
          
          // 라벨과 입력 필드 수직 배치 강제
          '.MuiTextField-root, .MuiFormControl-root': {
            width: '100% !important',
            marginBottom: '16px !important',
          },
          
          '.MuiInputLabel-root': {
            fontSize: '16px !important',
            marginBottom: '8px !important',
            display: 'block !important',
          },
          
          // Autocomplete 및 Select 크기 확대
          '.MuiAutocomplete-root, .MuiSelect-root': {
            minHeight: '44px !important',
            fontSize: '16px !important',
          },
          
          // 카드 및 컨테이너 패딩 조정 (수직 스택)
          '.MuiCard-root, .MuiPaper-root': {
            padding: '16px !important',
            marginBottom: '16px !important',
            width: '100% !important',
            boxSizing: 'border-box',
            borderRadius: '12px !important', // 더 둥근 모서리
          },
          
          // 카드 내부 요소 간격
          '.MuiCard-root > *, .MuiPaper-root > *': {
            marginBottom: '12px',
          },
          
          '.MuiCard-root > *:last-child, .MuiPaper-root > *:last-child': {
            marginBottom: 0,
          },
          
          // 테이블: 모바일에서는 가로 스크롤 가능하도록
          '.MuiTableContainer-root': {
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          },
          
          // 탭: 더 큰 터치 영역
          '.MuiTab-root': {
            minHeight: '48px !important',
            fontSize: '16px !important',
            padding: '12px 20px !important',
          },
          
          // 리스트 아이템: 더 큰 터치 영역
          '.MuiListItemButton-root, .MuiListItem-root': {
            minHeight: '48px !important',
            padding: '12px 16px !important',
            fontSize: '16px !important',
          },
          
          // 체크박스: 더 큰 터치 영역
          '.MuiCheckbox-root': {
            minWidth: '44px !important',
            minHeight: '44px !important',
            padding: '10px !important',
          },
          
          // 다이얼로그: 전체 화면에 가깝게
          '.MuiDialog-paper': {
            margin: '8px !important',
            maxHeight: 'calc(100% - 16px) !important',
            borderRadius: '16px !important',
            width: 'calc(100% - 16px) !important',
          },
          
          // 스크롤바: 모바일에서 더 두껍게
          '::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          
          // 하단 네비게이션 공간 확보
          '.mobile-content': {
            paddingBottom: '80px',
          },
          
          // Chip 및 Badge: 터치 친화적 크기
          '.MuiChip-root': {
            minHeight: '32px',
            fontSize: '14px',
            padding: '4px 12px',
          },
          
          // 아이콘 버튼: 최소 터치 영역 확보
          '.MuiIconButton-root': {
            padding: '10px !important',
          },
        },
      }}
    />
  );

  // 메인 콘텐츠 컴포넌트
  const MainContent = () => {
    const [showDailyTimeModal, setShowDailyTimeModal] = React.useState(false);

    // 알림 서비스 초기화 (앱 로드 시)
    React.useEffect(() => {
      // 알림 권한 요청
      notificationService.checkPermission();
      
      // 넛지 알림 기능 활성화 여부 (환경 변수 또는 설정으로 제어 가능)
      const enableNudgeNotifications = process.env.REACT_APP_ENABLE_NUDGE_NOTIFICATIONS !== 'false';
      
      if (enableNudgeNotifications) {
        // 퇴근 시간 넛지 스케줄러 시작 (오후 5:30)
        notificationService.startEndOfDayScheduler('17:30');
        
        // 출근 시간 캐치업 스케줄러 시작 (오전 9:00)
        notificationService.startMorningCatchupScheduler('09:00');
      }

      // 당일 투입시간 알림 모달 콜백 설정
      notificationService.setDailyTimeModalCallback(() => {
        setShowDailyTimeModal(true);
      });

      // 당일 투입시간 알림 스케줄러 시작 (오전 11시, 오후 4시)
      notificationService.startDailyTimeNotificationScheduler();

      // 컴포넌트 언마운트 시 정리
      return () => {
        notificationService.stopDailyTimeNotificationScheduler();
      };
    }, []);

    return (
      <MobileViewProvider>
        <UserRoleProvider>
          <LanguageProvider>
            <ProjectProvider>
              <DBConnectionProvider>
                <TaskProvider>
                  <NavigationProvider>
                    {/* 라우터 컴포넌트 (모바일/PC 분리) - MobileViewProvider 내부에서 사용 */}
                    <AppRouterInner />
                    {/* 당일 투입시간 알림 모달 */}
                    <DailyTimeNotificationModal
                      open={showDailyTimeModal}
                      onClose={() => setShowDailyTimeModal(false)}
                    />
                    {/* PWA 설치 프롬프트 */}
                    <PWAInstallPrompt />
                  </NavigationProvider>
                </TaskProvider>
              </DBConnectionProvider>
            </ProjectProvider>
          </LanguageProvider>
        </UserRoleProvider>
      </MobileViewProvider>
    );
  };

  // 라우터 컴포넌트 (모바일/PC 분리) - MobileViewProvider 내부에서 사용
  const AppRouterInner = () => {
    const { isMobileView, setIsMobileView } = useMobileView();
    const location = useLocation();
    const navigate = useNavigate();
    
    /**
     * ⚠️ 중요: Layout 선택 규칙 (변경 금지)
     * ============================================
     * 
     * 경로 기반으로 Layout을 선택합니다 (경로가 우선순위)
     * 
     * 규칙:
     *   - /m/* 경로 → MobileLayout (모바일 레이아웃)
     *   - /p/* 경로 → MainLayout (PC 레이아웃)
     *   - 기타 경로 → MainLayout (기본값)
     * 
     * ⚠️ 절대 변경 금지:
     *   - isMobileView 상태를 사용하지 않고 경로(/m/ 또는 /p/)를 기준으로 선택
     *   - 이는 routes.js의 라우팅 규칙과 일치해야 함
     *   - /p/dashboard는 MainLayout + Dashboard 컴포넌트
     *   - /m/dashboard는 MobileLayout + MobileDashboard 컴포넌트
     * 
     * 변경 이력:
     *   - V15: 경로 기반 선택으로 변경 (이전 버전에서 isMobileView 사용 시 문제 발생)
     *   - V15: 라우팅이 반대로 설정되어 있었던 문제를 수정함
     *   - V23: 모바일 디바이스 자동 감지 및 리다이렉트 추가
     * 
     * 자세한 내용은 ROUTING_RULES.md 문서를 참조하세요.
     */
    const isMobilePath = location.pathname.startsWith('/m/');
    const Layout = isMobilePath ? MobileLayout : MainLayout;
    // 토큰 유효성 검증을 포함한 로그인 상태 확인
    const isLoggedIn = checkAuthStatus();
    const isLoginPage = location.pathname === '/login';
    
    // 모바일 디바이스 감지 및 자동 리다이렉트
    React.useEffect(() => {
      if (isLoginPage || !isLoggedIn) return;
      
      // 모바일 디바이스 감지
      const isMobile = detectMobileDevice();
      
      // ⚠️ 중요: 모바일 디바이스는 항상 모바일 경로(/m/*)를 사용해야 함
      // 모바일 디바이스에서 PC 경로(/p/*) 또는 레거시 경로에 접근하면 강제로 모바일 경로로 리다이렉트
      if (isMobile && !isMobilePath) {
        const mobilePath = convertToMobilePath(location.pathname);
        if (mobilePath !== location.pathname) {
          console.log('[AppRouterInner] 모바일 디바이스 감지: PC 경로에서 모바일 경로로 리다이렉트', {
            from: location.pathname,
            to: mobilePath
          });
          navigate(mobilePath, { replace: true });
          return;
        }
      }
      
      // PC 디바이스인데 모바일 경로에 있으면 PC 경로로 리다이렉트 (단, 사용자가 수동으로 모바일 뷰를 선택한 경우 제외)
      if (!isMobile && isMobilePath) {
        const savedMobileView = localStorage.getItem('mobileView');
        const isManualMobileView = savedMobileView === 'true';
        
        // 사용자가 수동으로 모바일 뷰를 선택하지 않은 경우에만 PC 경로로 리다이렉트
        if (!isManualMobileView) {
          const pcPath = convertToPcPath(location.pathname);
          if (pcPath !== location.pathname) {
            console.log('[AppRouterInner] PC 디바이스 감지: 모바일 경로에서 PC 경로로 리다이렉트', {
              from: location.pathname,
              to: pcPath
            });
            navigate(pcPath, { replace: true });
            return;
          }
        }
      }
      
      // 모바일 경로일 때 body에 mobile-route 클래스 추가 (스크롤 허용)
      if (isMobilePath) {
        document.body.classList.add('mobile-route');
        setIsMobileView(true);
      } else {
        document.body.classList.remove('mobile-route');
        // 모바일 디바이스이지만 모바일 경로가 아닌 경우는 이미 위에서 리다이렉트되므로 여기서는 false로 설정
        if (!isMobile) {
          setIsMobileView(false);
        }
      }
    }, [location.pathname, isLoggedIn, isLoginPage, isMobilePath, setIsMobileView, navigate]);
    
    // 토큰이 만료되었거나 유효하지 않은 경우 데이터 정리
    React.useEffect(() => {
      if (!isLoggedIn && !isLoginPage) {
        clearAuthData();
        // 로그아웃 시 FCM 토큰 삭제
        notificationService.deleteTokenFromServer();
      }
    }, [isLoggedIn, isLoginPage]);

    // FCM 초기화 및 토큰 발급 (로그인 시)
    React.useEffect(() => {
      if (!isLoggedIn || isLoginPage) {
        return;
      }

      const initFCM = async () => {
        try {
          // 1. 권한 상태 확인 (브라우저에 저장된 값 확인)
          const permissionStatus = notificationService.checkNotificationPermission();
          
          // 2. 이미 승인된 경우: 자동으로 토큰 발급 (팝업 없음)
          if (permissionStatus.status === 'granted') {
            const token = localStorage.getItem('token');
            if (token) {
              const userInfo = getUserFromToken(token);
              const userId = userInfo?.employee_code || localStorage.getItem('employeeCode');
              if (userId) {
                // FCM 토큰 요청 (Firebase 설정이 없으면 자동으로 실패 처리됨)
                await notificationService.requestFCMToken(userId);
              }
            }
            return;
          }
          
          // 3. default 상태: 권한 요청 (최초 1회만 팝업 표시됨)
          // 사용자가 알림 권한을 허용하면 자동으로 토큰 발급됨
          // 권한 요청은 사용자가 명시적으로 요청할 때까지 보류 (선택적)
          if (permissionStatus.status === 'default' && process.env.NODE_ENV === 'development') {
            console.log('[App] 알림 권한 요청 가능. 사용자가 알림 설정에서 활성화할 수 있습니다.');
          }
          
          // 4. denied 상태: 브라우저 설정 변경 안내 (이미 checkNotificationPermission에서 처리됨)
        } catch (error) {
          // Firebase 설정이 없을 때 발생할 수 있는 오류는 무시
          if (process.env.NODE_ENV === 'development') {
            console.warn('[App] FCM 초기화 오류 (Firebase 설정 없음일 수 있음):', error.message);
          }
        }
      };

      initFCM();
    }, [isLoggedIn, isLoginPage]);

    // 라우터 상태 확인 (디버깅 필요 시에만 주석 해제)
    // React.useEffect(() => {
    //   console.log('[AppRouterInner] isMobileView:', isMobileView);
    //   console.log('[AppRouterInner] Layout:', Layout.name);
    //   console.log('[AppRouterInner] 현재 경로:', location.pathname);
    //   console.log('[AppRouterInner] 로그인 상태:', isLoggedIn);
    //   console.log('[AppRouterInner] isMobilePath:', isMobilePath);
    // }, [isMobileView, Layout, location.pathname, isLoggedIn, isMobilePath]);

    // 로그인 페이지는 레이아웃 없이 렌더링
    if (isLoginPage) {
      return (
        <Routes>
          {routes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              element={route.element}
            />
          ))}
        </Routes>
      );
    }

    // 그 외 페이지는 레이아웃과 함께 렌더링
    return (
      <Routes>
        <Route path="/" element={<Layout />}>
          {routes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              element={route.element}
            />
          ))}
        </Route>
      </Routes>
    );
  };

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {globalStyles}
      
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: showSplash ? 0 : 1,
          opacity: showSplash ? 0 : 1,
          transition: 'opacity 0.6s ease-in',
          pointerEvents: showSplash ? 'none' : 'auto'
        }}
      >
        <MainContent />
      </Box>
      
      {showSplash && (
        <LoadingSplash 
          key={isDark ? 'dark' : 'light'}
          duration={splashDuration}
          customStages={splashStages}
          message={isDark ? 'OnTimeLog 시스템 초기화 중...' : '시스템 초기화 중...'}
          onComplete={() => {
            setTimeout(() => {
              setShowSplash(false);
              setIsLoading(false);
              sessionStorage.setItem('skipAppSplash', 'true');
              
              // DB 상태가 연결되지 않은 경우 경고 표시 (선택적)
              if (dbStatus && !dbStatus.connected) {
                console.warn('⚠️ DB 연결 상태:', dbStatus);
                // 필요시 사용자에게 알림 표시 (선택적)
                // alert('데이터베이스 연결이 확인되지 않았습니다. 일부 기능이 제한될 수 있습니다.');
              }
            }, 100);
          }}
        />
      )}
    </MuiThemeProvider>
  );
}

// App - ThemeProvider로 감싸기
function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ThemeProvider>
        <OnboardingProvider>
          <OnboardingTutorial />
          <AppContent />
        </OnboardingProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
